import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';

const ASSURANCES = ['No hardware', 'No servers', 'No IT team'];

/**
 * The closing call to action.
 *
 * It used to be a saturated ocean-gradient banner with a sheen sweep, two
 * blur orbs, two vector shapes and a centred stack — every decorative
 * primitive the system owns, on one surface, above a heading that needed
 * none of them.
 *
 * It now stands on .card--mint, and it is the page's one LIGHT panel by
 * design. The offer card a section above is the dark anchor; a dark banner
 * under it and a dark footer under that turned the whole bottom of the page
 * into a single mass with the copy floating in it. A pale mint ground with
 * two soft blooms breaks that run, and it is the same green the pill and the
 * ticks are cut from, so the panel still reads as one hue.
 *
 * Centred-stack CTAs put the action last in the reading order and leave the
 * headline floating over dead width. The panel is split instead: the claim
 * on the left, the two actions banked on the right where the eye lands after
 * the line, and the fine print under them rather than under everything.
 *
 * Colour is the plane's — .card--mint re-points --color-text /
 * --color-text-muted / --color-border for its subtree, and hands
 * .display-accent the deep stop of its own green. The bright mint the dark
 * planes use for that word would wash out here, which is why the token pair
 * exists.
 */
const CTABanner = () => (
  <section className="section section--surface">
    <div className="container-page">
      {/* No orbs: the panel's own blooms are its depth. A blur orb needs a
          dark ground to glow against — on a pale one it is a smudge. */}
      <div className="accent-4 card card--mint !p-8 lg:!p-12">

        <div className="grid items-center gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-14">
          <div>
            <span className="chip">Beta · 1 month free</span>

            <h2 className="mt-5 text-section font-bold leading-[1.08] tracking-tight text-content">
              Your call center running in 10 minutes.{' '}
              <span className="display-accent">Free.</span>
            </h2>

            <p className="mt-4 max-w-xl text-lede leading-relaxed text-content-muted">
              Sign up, add your agents and start dialing today.
            </p>

            <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
              {ASSURANCES.map(item => (
                <li key={item} className="flex items-center gap-2 text-[0.9375rem] text-content-muted">
                  <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-white shadow-[0_0_5px_var(--color-accent-mint-deep)]">
                    <Check className="h-3 w-3 text-mint-deep" strokeWidth={3} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* The actions bank right on desktop and fall under the copy on
              mobile, where a side-by-side pair of full-width pills would
              each be too narrow to read. */}
          <div className="flex flex-col gap-3">
            <Link to="/contact" className="btn-pill btn-pill--success btn-pill--block">
              Claim free beta access
              <ArrowRight className="h-[18px] w-[18px]" />
            </Link>

            <Link to="/contact" className="btn-pill btn-pill--white btn-pill--block">
              Book a demo
            </Link>

            <p className="mt-2 text-center text-sm leading-relaxed text-content-muted">
              No credit card · No automatic charges after beta
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default CTABanner;
